import {HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {BehaviorSubject, catchError, Observable, switchMap, throwError} from 'rxjs';
import {inject} from '@angular/core';
import {AuthService} from '../services/auth.service';

const refreshing = new BehaviorSubject<boolean>(false);


/**
 * A custom HTTP interceptor function that manages authentication and authorization for outgoing HTTP requests.
 * It ensures cookies are sent with requests and handles HTTP 401 Unauthorized responses by attempting to refresh the authentication token.
 *
 * The interceptor performs the following tasks:
 * - Clones the outgoing HTTP request to include credentials (e.g., cookies).
 * - Passes the modified request to the next handler in the chain.
 * - Catches HTTP 401 errors and attempts to refresh the token using the `AuthService`.
 * - If token refresh is successful, retries the original request.
 * - Logs out the user if the token refresh fails.
 *
 * @type {HttpInterceptorFn}
 * @param {HttpRequest<unknown>} req - The HTTP request being intercepted.
 * @param {HttpHandlerFn} next - The next handler in the HTTP pipeline.
 * @return {Observable<HttpEvent<unknown>>} - An observable that emits the HTTP event or an error if the intercepted operation fails.
 */
export const authInterceptor: HttpInterceptorFn = (
  req,
  next
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  // Ensure cookies are sent with requests
  const authReq = req.clone({ withCredentials: true });

  return next(authReq).pipe(
    catchError((error) => {
      if (error.status === 401) {
        return handle401Error(authService, req, next);
      }
      return throwError(() => error);
    })
  );

  /**
   * Handles HTTP 401 errors by attempting to refresh the authentication token.
   * If a refresh is already in progress, queues the request until the refresh is complete.
   *
   * @param {AuthService} authService - The service responsible for handling authentication-related actions, such as token refresh or logout.
   * @param {HttpRequest<unknown>} req - The HTTP request that encountered the 401 error.
   * @param {HttpHandlerFn} next - The function that handles the forwarding of the modified HTTP request.
   * @return {Observable<HttpEvent<unknown>>} - An observable that emits the HTTP event for the retried request or an error if the process fails.
   */
  function handle401Error(authService: AuthService, req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    if (!refreshing.value) {
      refreshing.next(true);

      return authService.refreshToken().pipe(
        switchMap(() => {
          refreshing.next(false);
          return next(req.clone({ withCredentials: true }));
        }),
        catchError((error) => {
          console.error('Token refresh failed', error);
          refreshing.next(false);
          authService.cleaSessionAndLogout();
          return throwError(() => error);
        })
      );
    } else {
      return refreshing.pipe(
        switchMap(() => next(req.clone({ withCredentials: true })))
      );
    }
  }
};
